import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiPost, apiPut, apiGet } from "@/lib/api-client";

interface CreateLeaderboardBody {
  name: string;
  description?: string;
  period: "weekly" | "monthly" | "alltime";
  sortOrder?: "asc" | "desc";
  maxEntries?: number;
}

interface UpdateLeaderboardBody {
  name?: string;
  description?: string;
  sortOrder?: "asc" | "desc";
  maxEntries?: number;
}

interface Leaderboard {
  id: string;
  venueId: string;
  name: string;
  description: string | null;
  period: "weekly" | "monthly" | "alltime";
  status: "active" | "archived";
  sortOrder: "asc" | "desc";
  maxEntries: number;
  createdAt: string;
  updatedAt: string;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

export function useCreateLeaderboard(venueId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateLeaderboardBody) => {
      if (!venueId) throw new Error("No venue ID");
      const response = await apiPost<ApiResponse<Leaderboard>>(
        `/venues/${venueId}/leaderboards`,
        body as unknown as Record<string, unknown>
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leaderboards", venueId] });
    },
  });
}

export function useUpdateLeaderboard(venueId: string | null | undefined, boardId: string | null | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateLeaderboardBody) => {
      if (!venueId || !boardId) throw new Error("No venue ID or board ID");
      const response = await apiPut<ApiResponse<Leaderboard>>(
        `/venues/${venueId}/leaderboards/${boardId}`,
        body as unknown as Record<string, unknown>
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["leaderboards", venueId] });
    },
  });
}

export function useLeaderboard(venueId: string | null | undefined, boardId: string | null | undefined) {
  return useQuery({
    queryKey: ["leaderboard", venueId, boardId],
    queryFn: async () => {
      if (!venueId || !boardId) return null;
      const response = await apiGet<ApiResponse<Leaderboard>>(
        `/venues/${venueId}/leaderboards/${boardId}`
      );
      return response.data;
    },
    enabled: !!venueId && !!boardId,
  });
}
